import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { euroFormat } from '../../helpers/functions';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import logo from '../../images/logo.svg'; // Import your logo directly

const ComparaisonCaMois = ({ data }) => {
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [chartType, setChartType] = useState('bar');
    const chartRef = useRef(null);

    useEffect(() => {
        if (data && data.current && data.previous) {
            const moisEnFrancais = [
                "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
            ];

            const uniqueMonths = Array.from(new Set(data.current.map(item => item.mois)));
            // Mois présents dans l'année en cours (sous forme de string "01", "02", etc.)
            const moisCourants = data.current.map(item => item.mois);

            // Filtrer les données de l’année précédente pour ne garder que les mêmes mois
            const previousFiltree = data.previous.filter(item => moisCourants.includes(item.mois));

            // Calcul des totaux en comparant les mêmes périodes
            const totalCurrent = data.current.reduce((acc, item) => acc + parseFloat(item.totalCA || 0), 0);
            const totalPrevious = previousFiltree.reduce((acc, item) => acc + parseFloat(item.totalCA || 0), 0);


            const updatedChartData = {
                labels: uniqueMonths.map(month => moisEnFrancais[parseInt(month) - 1]),
                datasets: [
                    {
                        label: `CA ${data.currentYear}: ${euroFormat(totalCurrent)}`,
                        backgroundColor: "rgba(75,192,192,0.4)",
                        borderColor: "rgba(75,192,192,1)",
                        hoverBackgroundColor: "rgba(75,192,192,0.6)",
                        hoverBorderColor: "rgba(75,192,192,1)",
                        data: uniqueMonths.map(month => {
                            const totalCurrentCA = data.current.find(item => item.mois === month);
                            return totalCurrentCA ? parseFloat(totalCurrentCA.totalCA) : 0;
                        }),
                        stack: 'Stack 0',
                    },
                    {
                        label: `CA ${data.previousYear}: ${euroFormat(totalPrevious)}`,
                        backgroundColor: "rgba(255,99,132,0.4)",
                        borderColor: "rgba(255,99,132,1)",
                        hoverBackgroundColor: "rgba(255,99,132,1)",
                        hoverBorderColor: "rgba(255,99,132,1)",
                        data: uniqueMonths.map(month => {
                            const totalPreviousCA = data.previous.find(item => item.mois === month);
                            return totalPreviousCA ? parseFloat(totalPreviousCA.totalCA) : 0;
                        }),
                        stack: 'Stack 1',
                    },
                ]
            };

            setChartData(updatedChartData);
        }
    }, [data]);

    const handleChangeChartType = (type) => {
        setChartType(type);
    };

    const options = {
        scales: {
            xAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: "Mois"
                },
                stacked: true,
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: "CA HT"
                },
                ticks: {
                    beginAtZero: true,
                },
                stacked: true,
            }]
        },
        title: {
            display: true,
            text: `Comparaison CA HT par Mois (${data?.currentYear} vs ${data?.previousYear})`,
            fontSize: 18, // Increased title font size
        },
        legend: { // Configuration for the legend (which includes dataset labels)
            display: true,
            labels: {
                fontSize: 13, // Increased font size for dataset labels (e.g., "CA 2023")
            }
        },
        tooltips: {
            enabled: false,
        },
        hover: {
            mode: null,
            animationDuration: 0,
        },
        elements: {
            line: {
                fill: false,
            },
            point: {
                radius: 0
            }
        },
        // Suppression de l'affichage manuel des totaux au-dessus des barres
    };

    const handlePrintChart = useCallback(() => {
        if (!chartRef.current) return;

        const chartCanvas = chartRef.current.chartInstance.canvas;
        const image = chartCanvas.toDataURL('image/png');

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Comparaison CA HT par Mois</title>
                <style>
                    body {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        flex-direction: column; /* To stack logo and chart */
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                    }
                    .logo {
                        max-width: 200px; /* Adjust logo size as needed */
                        margin-bottom: 60px; /* Space between logo and chart */
                        height: auto; /* Maintain aspect ratio */
                    }
                    @page {
                        margin: 10mm;
                    }
                    img {
                        max-width: 100%;
                        max-height: calc(100vh - 20mm);
                        height: auto;
                        display: block;
                    }
                    @media print {
                        body {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                </style>
            </head>
            <body>
                <img src="${logo}" alt="Your Logo" class="logo" />
                <img src="${image}" />
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        };
    }, []);

    return (
        <div style={{ textAlign: 'center' }}>
            <div className="no-print">
                <ButtonGroup variant="outlined" aria-label="Basic button group">
                    <Button onClick={() => handleChangeChartType('bar')}>Barres</Button>
                    <Button onClick={() => handleChangeChartType('line')}>Lignes</Button>
                </ButtonGroup>
                <Button onClick={handlePrintChart} variant="contained" color="primary" style={{ marginLeft: '10px' }}>
                    Imprimer le graphique
                </Button>
            </div>
            {chartType === 'bar' ? (
                <Bar ref={chartRef} data={chartData} options={options} height={80} />
            ) : (
                <Line ref={chartRef} data={chartData} options={options} height={80} />
            )}
        </div>
    );
};

export default ComparaisonCaMois;